import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "@Hooks/usePageTitle";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Heading,
  Text,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
} from "@chakra-ui/react";
import { IconSearch } from "@tabler/icons-react";
import AppTable, { Column } from "@Components/Table";
import { Button } from "@Components/Button";
import { FiltroBar } from "@Components/FiltroBar";
import { formatCurrency } from "@Utils/index";
import { listSimulacoes, SimulacaoInternaItem } from "@Api/estudo";

const PAGE_SIZE = 50;

// TODO(permissão): gate com "diagnostico.simulacoes.read" quando o código existir.

const num0 = (n: number | null) =>
  n == null ? "—" : n.toLocaleString("pt-BR", { maximumFractionDigits: 0 });

// Larguras enxutas (somam ~840px) + razão social truncada: cabe sem scroll horizontal.
const columns: Column[] = [
  {
    label: "Autor",
    key: "consultorNome",
    width: "130px",
    render: (v: string | null) => (
      <Text
        fontSize="13px"
        fontWeight="600"
        color="neutrals.text.strong-950"
        fontFamily="body"
        noOfLines={1}
      >
        {v || "—"}
      </Text>
    ),
  },
  { label: "Data", key: "generatedAt", type: "datetime", width: "150px" },
  {
    // sem width → absorve o espaço restante (tableLayout: fixed)
    label: "Razão social",
    key: "razaoSocial",
    render: (v: string | null) => (
      <Text fontSize="13px" color="neutrals.text.strong-950" fontFamily="body" noOfLines={1}>
        {v || "—"}
      </Text>
    ),
  },
  {
    label: "Litros",
    key: "litrosTotais",
    width: "92px",
    render: (v: number | null) => (
      <Text fontSize="13px" color="neutrals.text.strong-950" fontFamily="body">
        {num0(v)}
      </Text>
    ),
  },
  {
    label: "Economia/mês",
    key: "economiaMensal",
    width: "120px",
    render: (v: number | null) => (
      <Text
        fontSize="13px"
        fontWeight="600"
        color={v && v > 0 ? "states.success.dark" : "neutrals.text.soft-400"}
        fontFamily="body"
      >
        {v && v > 0 ? formatCurrency(v) : "—"}
      </Text>
    ),
  },
  {
    label: "Convertido",
    key: "convertido",
    width: "120px",
    render: (v: boolean, item: SimulacaoInternaItem) => {
      // Cliente da base: já é cliente — selo azul "Cliente base" (não "convertido").
      if (item.origem === "cliente") {
        return (
          <Text
            fontSize="12px"
            fontWeight="600"
            color="#1D4ED8"
            bg="#EFF6FF"
            px="8px"
            py="2px"
            borderRadius="full"
            display="inline-block"
            fontFamily="body"
          >
            Cliente base
          </Text>
        );
      }
      // Prospect cujo CNPJ virou cliente → selo verde "Convertido".
      return v ? (
        <Text
          fontSize="12px"
          fontWeight="600"
          color="states.success.dark"
          bg="states.success.lighter"
          px="8px"
          py="2px"
          borderRadius="full"
          display="inline-block"
          fontFamily="body"
        >
          Convertido
        </Text>
      ) : (
        <Text fontSize="13px" color="neutrals.text.soft-400" fontFamily="body">
          —
        </Text>
      );
    },
  },
];

export const HistoricoPage: FC = () => {
  usePageTitle("Histórico");
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [page, setPage] = useState(0);

  // Debounce do filtro (400ms) + volta p/ a 1ª página quando o filtro muda.
  useEffect(() => {
    const t = setTimeout(() => {
      setQDebounced(q.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["simulacoes-internas", qDebounced, page],
    queryFn: () =>
      listSimulacoes({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        q: qDebounced || undefined,
      }),
    // Sempre refaz o fetch ao abrir o Histórico (senão o cache de 5min do app
    // esconde a análise recém-criada até sair e voltar).
    staleTime: 0,
    refetchOnMount: "always",
    // Mantém a página anterior visível durante a troca (evita piscar).
    placeholderData: (prev) => prev,
  });

  const total = data?.total ?? 0;
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);
  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < total;

  return (
    <Box>
      <Heading fontSize="20px" fontFamily="heading" color="neutrals.text.strong-950">
        Histórico
      </Heading>
      <Text mt="4px" mb="16px" fontSize="sm" color="neutrals.text.sub-600" fontFamily="body">
        Análises geradas pelo time comercial. Clique em uma linha para abrir.
      </Text>

      {/* Barra de filtros (padrão Cardan) */}
      <FiltroBar>
        <InputGroup maxW="420px" w="100%">
          <InputLeftElement pointerEvents="none">
            <Icon as={IconSearch} boxSize={4} color="neutrals.icon.soft-400" />
          </InputLeftElement>
          <Input
            placeholder="Filtrar por razão social, CNPJ ou autor"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            borderRadius="8px"
            fontFamily="body"
          />
        </InputGroup>
      </FiltroBar>

      <AppTable
        noScroll
        columns={columns}
        data={data?.items}
        isLoading={isLoading}
        emptyStateTitle={
          isError ? "Não foi possível carregar" : "Nenhuma análise ainda"
        }
        emptyStateMessage={
          isError
            ? "Tente novamente em instantes."
            : qDebounced
              ? "Nenhum resultado para o filtro."
              : "As análises geradas aparecerão aqui."
        }
        onRowClick={(item) =>
          navigate(`/resultado/${(item as SimulacaoInternaItem).token}`)
        }
      />

      {/* Paginação (50 por página) */}
      <Flex mt="16px" align="center" justify="space-between" gap="12px" wrap="wrap">
        <Text fontSize="sm" color="neutrals.text.sub-600" fontFamily="body">
          {total > 0 ? `${from}–${to} de ${total}` : "—"}
        </Text>
        <HStack spacing="8px">
          <Button
            colorSchema="neutral"
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            isDisabled={!hasPrev}
          >
            Anterior
          </Button>
          <Button
            colorSchema="neutral"
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            isDisabled={!hasNext}
          >
            Próxima
          </Button>
        </HStack>
      </Flex>
    </Box>
  );
};
